import React from 'react';
import type { Transition, Variants } from 'framer-motion';
import { motion } from 'framer-motion';
import { XIcon } from '@phosphor-icons/react';
import { Button } from '@tranquility-ai/theme-components/button';
import { TpTooltip } from '@tranquility-ai/theme-components/tooltip';
import { cn } from '@tranquility-ai/theme-components/utils';

import './side-panel.css';

export type SidePanelSide = 'left' | 'right';

/**
 * Shared slide-in/out variants for the side panels. `left` slides from the left
 * edge, `right` from the right edge, so the Session History (left) and Favorites
 * (right) panels animate with the same pattern. Pair with `<AnimatePresence>` on
 * the caller so the exit transition plays on unmount.
 */
export function sidePanelSlideVariants(side: SidePanelSide): Variants {
  const offset = side === 'left' ? '-100%' : '100%';
  return {
    initial: { x: offset },
    animate: { x: 0 },
    exit: { x: offset },
  };
}

/** Spring transition shared by both panels. */
export const SIDE_PANEL_TRANSITION = {
  type: 'spring' as const,
  damping: 30,
  stiffness: 300,
};

/** Default docked width for Session History and Favorites (~1/5 viewport). */
export const SIDE_PANEL_WIDTH_PX = 330;

/**
 * Session History and Favorites panels: fixed below the primary (blue) nav bar
 * and running to the bottom of the viewport, inset with spacing tokens
 * (`--spacing-large` chrome gap, `--spacing-xlarge` viewport inset).
 *
 * Pair with {@link SIDE_PANEL_SHELL_LEFT_CLASS} or
 * {@link SIDE_PANEL_SHELL_RIGHT_CLASS} and {@link SIDE_PANEL_ABOVE_PRIMARY_NAV_Z_CLASS}.
 */
export const SIDE_PANEL_SHELL_CLASS = 'tp-side-panel-shell';
export const SIDE_PANEL_SHELL_LEFT_CLASS = 'tp-side-panel-shell--left';
export const SIDE_PANEL_SHELL_RIGHT_CLASS = 'tp-side-panel-shell--right';

/** @deprecated Use {@link SIDE_PANEL_SHELL_CLASS} with side modifier classes. */
export const SIDE_PANEL_BELOW_NAV_CLASS = SIDE_PANEL_SHELL_CLASS;

/** Backdrop for portaled side panels that must cover the primary nav bar. */
export const SIDE_PANEL_ABOVE_PRIMARY_NAV_BACKDROP_Z_CLASS = 'z-[100001]';

/** Panel surface above {@link SIDE_PANEL_ABOVE_PRIMARY_NAV_BACKDROP_Z_CLASS}. */
export const SIDE_PANEL_ABOVE_PRIMARY_NAV_Z_CLASS = 'z-[100002]';

/**
 * Portaled popovers (e.g. Combobox listbox on `document.body`) that must paint
 * above {@link SIDE_PANEL_ABOVE_PRIMARY_NAV_Z_CLASS}. Matches Combobox `navPill`
 * stacking (`100003`). Default Combobox menus sit at `100000`, which is behind
 * this panel — without the bump, Saved/date-range options open invisibly.
 */
export const SIDE_PANEL_ABOVE_PRIMARY_NAV_POPOVER_Z_CLASS = 'z-[100003]';

/**
 * Blocking content-moderation acknowledgement modal. Must paint above
 * {@link SIDE_PANEL_ABOVE_PRIMARY_NAV_POPOVER_Z_CLASS} so Favorites/Session
 * History backdrop blur cannot obscure the warning dialog.
 */
export const CONTENT_MODERATION_ACK_MODAL_Z_CLASS = 'z-[100004]';

/** Master/detail vertical split border (Heliotrace pane resizer line at rest). */
export const SIDE_PANEL_SPLIT_BORDER_CLASS =
  'border-[var(--tp-side-panel-split-border)]';

/** Horizontal section rule — same token as {@link SIDE_PANEL_SPLIT_BORDER_CLASS}. */
export const SIDE_PANEL_DIVIDER_BOTTOM_CLASS =
  `border-b ${SIDE_PANEL_SPLIT_BORDER_CLASS}`;

/** @deprecated Use SIDE_PANEL_BELOW_NAV_CLASS — kept for callers that still import the name. */
export const SIDE_PANEL_FULL_HEIGHT_CLASS = SIDE_PANEL_BELOW_NAV_CLASS;

/** Body/content padding aligned with Figma Side Panel (684:2755). */
export const SIDE_PANEL_BODY_INSET_CLASS = 'tp-side-panel__body-inset';

/** Horizontal body inset only (for stacked sections inside the panel body). */
export const SIDE_PANEL_BODY_INSET_X_CLASS = 'tp-side-panel__body-inset-x';

/** Scrollable list region with standard horizontal inset for cards. */
export const SIDE_PANEL_LIST_SCROLL_CLASS = 'tp-side-panel__list-scroll';

/** Scrollable list region with symmetric card gutters (Session History). */
export const SIDE_PANEL_LIST_SCROLL_SYMMETRIC_CLASS = 'tp-side-panel__list-scroll--symmetric';

/** Panel modifier: equal left/right body inset for section labels and list scroll. */
export const SIDE_PANEL_SYMMETRIC_INSET_CLASS = 'tp-side-panel--symmetric-inset';

/**
 * Favorites master column (Figma Side Panel Application2): 16px vertical inset
 * vs 8px for Session History.
 */
export const SIDE_PANEL_FAVORITES_LIST_SCROLL_CLASS =
  'tp-side-panel__list-scroll--favorites';

/** Detail column top inset — matches favorites master list (16px). */
export const SIDE_PANEL_DETAIL_INSET_CLASS = 'tp-side-panel__detail-inset';

/** Vertical stack spacing for PanelListCard rows. */
export const SIDE_PANEL_LIST_CLASS = 'tp-side-panel__list';

/** Inner slide layer — transform must not be applied to {@link SIDE_PANEL_SHELL_CLASS}. */
export const SIDE_PANEL_SHELL_SLIDE_CLASS = 'tp-side-panel-shell__slide';

/**
 * Scrim geometry for an open side panel: full viewport except the primary nav,
 * so the nav is never blurred. Use instead of `fixed inset-0`.
 */
export const SIDE_PANEL_BACKDROP_CLASS = 'tp-side-panel-backdrop';

/**
 * Viewport dock geometry. Applied inline (not via utilities) because the app
 * sets Tailwind `important: true`, so utility classes would otherwise win over
 * the shell stylesheet. Values read the `--tp-side-panel-*` variables declared
 * on the shell class, which the stylesheet re-points per breakpoint.
 */
function sidePanelShellGeometry(side: SidePanelSide): React.CSSProperties {
  const inset = 'var(--tp-side-panel-viewport-inset)';

  return {
    position: 'fixed',
    top: 'calc(var(--tp-side-panel-nav-offset) + var(--tp-side-panel-chrome-gap))',
    height:
      'calc(100dvh - var(--tp-side-panel-nav-offset) - var(--tp-side-panel-chrome-gap)' +
      ' - var(--tp-side-panel-viewport-inset))',
    ...(side === 'left' ? { left: inset } : { right: inset }),
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  };
}

export interface SidePanelShellProps {
  side: SidePanelSide;
  className?: string;
  style?: React.CSSProperties;
  slideTransition?: Transition;
  role?: string;
  'aria-modal'?: boolean | 'false';
  'aria-label'?: string;
  children: React.ReactNode;
}

/**
 * Fixed viewport dock for Session History / Favorites, spanning from below the
 * primary nav to the bottom of the viewport. The outer shell owns position +
 * height; an inner motion layer owns the slide transform so Framer Motion does
 * not collapse `position: fixed` sizing.
 */
export function SidePanelShell({
  side,
  className,
  style,
  slideTransition = SIDE_PANEL_TRANSITION,
  role = 'dialog',
  'aria-modal': ariaModal = false,
  'aria-label': ariaLabel,
  children,
}: SidePanelShellProps) {
  const edgeClass =
    side === 'left' ? SIDE_PANEL_SHELL_LEFT_CLASS : SIDE_PANEL_SHELL_RIGHT_CLASS;

  return (
    <motion.div
      role={role}
      aria-modal={ariaModal}
      aria-label={ariaLabel}
      className={cn(
        SIDE_PANEL_SHELL_CLASS,
        edgeClass,
        'shadow-lg outline-none',
        className,
      )}
      style={{ ...sidePanelShellGeometry(side), ...style }}
      initial={false}
      exit={{ opacity: 1, transition: { duration: 0.35 } }}
    >
      <motion.div
        className={SIDE_PANEL_SHELL_SLIDE_CLASS}
        variants={sidePanelSlideVariants(side)}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={slideTransition}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}

export interface SidePanelProps {
  /** Which edge the panel is docked to. Rounds the corners facing the content. */
  side: SidePanelSide;
  title: React.ReactNode;
  /** Optional secondary line under the title (e.g. the case name). */
  subtitle?: React.ReactNode;
  /** Optional decorative icon rendered before the title. */
  leadingIcon?: React.ReactNode;
  /** Extra header controls rendered before the collapse button (e.g. a filter toggle). */
  headerActions?: React.ReactNode;
  onCollapse: () => void;
  collapseLabel: string;
  /** Override the default side-aware collapse glyph. */
  collapseIcon?: React.ReactNode;
  /** Optional section heading (e.g. "Recent") pinned between the header and the scrollable body. */
  sectionLabel?: React.ReactNode;
  /** Applied to the scrollable body wrapper. */
  contentClassName?: string;
  /** Applied to the outer `<aside>` (e.g. per-edge borders, rounding). */
  className?: string;
  'aria-label'?: string;
  children: React.ReactNode;
}

/**
 * Token-styled shell shared by the Session History and Favorites side panels:
 * a filled header bar (leading icon + title + optional subtitle + action slot +
 * close button), an optional count row, and a flexible body. The outer
 * container's position and slide animation are owned by the caller (see
 * `sidePanelSlideVariants`); this component provides the inner chrome only.
 *
 * Chrome follows the Figma Side Panel (file JrrgtQP103tCv472rMDKMj, node
 * 684:2755): an ink-085 body under an ink-075 header bar.
 */
export const SidePanel: React.FC<SidePanelProps> = ({
  side,
  title,
  subtitle,
  leadingIcon,
  headerActions,
  onCollapse,
  collapseLabel,
  collapseIcon,
  sectionLabel,
  contentClassName,
  className,
  'aria-label': ariaLabel,
  children,
}) => {
  return (
    <aside
      className={cn(
        'tp-side-panel',
        side === 'left' ? 'tp-side-panel--left' : 'tp-side-panel--right',
        className,
      )}
      aria-label={ariaLabel}
    >
      <header className="tp-side-panel__header">
        <div className="flex min-w-0 flex-col">
          <div className="tp-side-panel__title-row">
            {leadingIcon}
            <h2 className="tp-side-panel__title">{title}</h2>
          </div>
          {subtitle != null && subtitle !== '' && (
            <span className="tp-side-panel__subtitle">{subtitle}</span>
          )}
        </div>
        <div className="tp-side-panel__header-actions">
          {headerActions}
          <TpTooltip content={collapseLabel} delayDuration={0} sideOffset={4}>
            <Button
              iconOnly
              size="sm"
              style="text"
              color="light"
              background="inverse"
              onClick={onCollapse}
              aria-label={collapseLabel}
            >
              {collapseIcon ?? <XIcon weight="bold" />}
            </Button>
          </TpTooltip>
        </div>
      </header>

      {sectionLabel != null && sectionLabel !== '' && (
        <p className="tp-side-panel__section-label">{sectionLabel}</p>
      )}

      <div className={cn('tp-side-panel__body', contentClassName)}>
        {children}
      </div>
    </aside>
  );
};
